import React, { Children, FC, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { x } from '@xstyled/styled-components';
import { CarouselItem } from './CarouselItem';
import {
  CarouselVariants,
  useCarouselStoreForSequence
} from '../CarouselStore';
import { IconButton } from './IconButton';
import { ProgressBar } from './ProgressBar';
import { Wrapper } from './Wrapper';
import { useSequenceStore } from '~/training/sequence/SequenceStore';
import audioEffect from '~/assets/mp3/card_flip.mp3';
import { useAudioEffect } from '~/ui/audio-effect/useAudioEffect';
import { useGlobalMedia } from '~/ui/video-player/useGlobalMedia';
import { useGlobalMediaStore } from '~/ui/video-player/GlobalMediaProvider';
import { t } from 'ttag';
import {
  useEditorStore,
  SequenceItem,
  SubItemStatePayload,
  ServerItemResponse
} from '~/shell/Store';
import LightButton from '~/editor/buttons/LightButton';
import { TrashIconForBtn } from '~/training/sequence/templates/items';
import styled from 'styled-components';
import Popup from '~/editor/filePicker/components/Popup';
import { CloseButton } from '~/ui/buttons/CloseButton';
import { Button } from '~/ui/buttons/Button';
import DragAndDropIcon from '~/assets/button/dragAndDrop.svg';
import { useQuizStore } from '~/training/sequence/templates/items/quiz/QuizStore';

const DragAndDropIconForBtn = styled(DragAndDropIcon)`
  cursor: move;
`;

const PopupWrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 9999;
  background-color: rgba(0, 0, 0, 0.5);

  .reorder-popup {
    width: 400px;
    max-width: 400px;
    min-width: 400px;
    height: auto;
    justify-content: flex-start;
    max-height: 80%;

    .reorder-popup-header {
      display: flex;
      align-items: center;
      justify-content: center;
      margin-bottom: 10px;

      b {
        font-family: ralewayBold;
        font-size: 16px;
        font-style: normal;
        font-weight: 700;
      }

      button {
        margin-left: auto;
        position: relative;
        margin-top: -50px;
        margin-right: 20px;
      }
    }

    .reorder-popup-content {
      overflow: auto;
      margin-bottom: 10px;

      button {
        width: 100%;
        font-family: ralewayBold;
        margin-top: 10px;
        padding: 10px;
        pointer-events: auto;
      }
    }

    .doneButton {
      width: 100%;
      font-family: ralewayBold;
      margin-top: 10px;
      padding: 10px;
      pointer-events: auto;
    }
  }
`;

const ReorderComponentWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 15px;
  padding-left: 0px;
  padding-right: 0px;
  cursor: move;
  transition: all 0.2s ease;
  border-radius: 5px;

  &:hover {
    background-color: #f0f0f0;
  }

  div {
    border-radius: 5px;
    background: var(--Grey-2, #f5f5f5);
    width: 100%;
    color: #6f6f6f;
    font-size: 12px;
    padding: 10px 15px;
  }

  .reorder-component-title {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const ReorderComponent = ({
  title,
  currentPosition,
  draggedIndex,
  dragOverIndex,
  onDragStart,
  onDragOver,
  onDragEnd,
  onDrop
}: {
  title: string;
  currentPosition: number;
  draggedIndex: number | null;
  dragOverIndex: number | null;
  onDragStart: (e: React.DragEvent, index: number) => void;
  onDragOver: (e: React.DragEvent, index: number) => void;
  onDragEnd: () => void;
  onDrop: (e: React.DragEvent, index: number) => void;
}) => {
  const isDragging = draggedIndex === currentPosition;
  const isDragOver = dragOverIndex === currentPosition;

  return (
    <ReorderComponentWrapper
      draggable
      onDragStart={e => onDragStart(e, currentPosition)}
      onDragOver={e => onDragOver(e, currentPosition)}
      onDragEnd={onDragEnd}
      onDrop={e => onDrop(e, currentPosition)}
      style={{
        opacity: isDragging ? 0.5 : 1,
        backgroundColor: isDragOver ? '#f3ecff' : 'transparent',
        border: isDragOver ? '2px dashed #9845FB' : 'none'
      }}
    >
      <DragAndDropIconForBtn />
      <div className="reorder-component-title">
        {title?.replace(/<[^>]*>/g, '')}
      </div>
    </ReorderComponentWrapper>
  );
};

let indexForStart = 0;
const MAX_CARDS_COUNT = 6;

export const CarouselComponent: FC<{
  variant: CarouselVariants;
  id: string;
  header?: { title: string; subtitle: string; icon: string };
  clearQuizFeedbackOnChange?: boolean;
  cssStylesForItem?: string | Record<string, string>;
  showProgressBar?: boolean;
  itemType?: 'QUESTION' | 'CARD';
  newItemFields?: Record<string, string | null>;
}> = ({
  variant,
  children,
  id,
  header,
  clearQuizFeedbackOnChange,
  cssStylesForItem,
  showProgressBar = true,
  itemType = 'CARD',
  newItemFields
}) => {
  const getTypeName = (type: 'QUESTION' | 'CARD'): string => {
    return type === 'QUESTION' ? 'Question' : 'Card';
  };

  const matchesItemType = (
    item: SequenceItem,
    targetType: 'QUESTION' | 'CARD'
  ): boolean => {
    const typeName = getTypeName(targetType);
    return item.__typename === typeName || item.type === targetType;
  };

  const sequenceId = id;
  const { index, next, prev, setTotal, setVariant, setIndex } =
    useCarouselStoreForSequence(sequenceId);

  const childArray = Children.toArray(children);
  const { addBlocker, removeBlocker } = useSequenceStore();
  const [playAudioEffect] = useAudioEffect(audioEffect);
  const [showPopup, setShowPopup] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [localCardOrder, setLocalCardOrder] = useState<
    { id: number | string; position: number }[]
  >([]);
  const [disabledBtn, setDisabledBtn] = useState(false);
  const { setQuizFeedback } = useQuizStore();

  const {
    editorMode,
    contentEditable,
    sequence,
    removeSubItem,
    addSubItem,
    applySubItemsToState,
    setSequence,
    apiService
  } = useEditorStore();

  useEffect(() => {
    if (showPopup) {
      const cardItems = sequence?.items
        .filter((item: SequenceItem) => matchesItemType(item, itemType))
        .map((item: SequenceItem, cardIndex: number) => ({
          id: item.id,
          position: cardIndex + 1
        }));
      setLocalCardOrder(cardItems ?? []);
    }
  }, [showPopup, sequence?.items]);

  const reorderItems = (fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex) return;

    const newOrder = [...localCardOrder];
    const [draggedItem] = newOrder.splice(fromIndex, 1);
    newOrder.splice(toIndex, 0, draggedItem);

    const updatedOrder = newOrder.map((item, index) => ({
      ...item,
      position: index + 1
    }));

    setLocalCardOrder(updatedOrder);
  };

  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    e.stopPropagation();
    if (draggedIndex !== null && draggedIndex !== dropIndex) {
      reorderItems(draggedIndex, dropIndex);
    }
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const applyChanges = async () => {
    setDisabledBtn(true);

    for (const item of localCardOrder) {
      await apiService?.reorderSubItem(item.id, item.position);
    }

    if (apiService) {
      await apiService.getSequenceInfo(sequence!.id).then(updatedSequence => {
        setSequence(updatedSequence.sequence);
      });
    }

    setDisabledBtn(false);
    setShowPopup(false);
  };

  useEffect(() => {
    if (
      variant === 'grid' ||
      variant === 'dynamic' ||
      index === childArray.length - 1
    )
      return;
    addBlocker(id);
    return () => removeBlocker(id);
  }, [variant, index]);

  useEffect(() => {
    if (clearQuizFeedbackOnChange) {
      setQuizFeedback('');
    }
  }, [index, clearQuizFeedbackOnChange, setQuizFeedback]);

  useEffect(() => {
    if (childArray.length > 0 && index >= 0 && index < childArray.length) {
      indexForStart = index;
    }
  }, [index, childArray.length]);

  useEffect(() => {
    if (
      childArray.length > 0 &&
      indexForStart >= 0 &&
      indexForStart < childArray.length
    ) {
      if (index !== indexForStart) {
        setIndex(indexForStart);
      }
    } else if (childArray.length > 0 && index >= childArray.length) {
      const validIndex = Math.max(0, childArray.length - 1);
      setIndex(validIndex);
      indexForStart = validIndex;
    }
  }, [sequence?.items, childArray.length]);

  useEffect(() => {
    setTotal(childArray.length);
    setVariant(variant);
    setIndex(0);

    if (indexForStart > childArray.length - 1) {
      indexForStart = 0;
    }

    if (indexForStart > 0) {
      setIndex(indexForStart);
    }
  }, [variant]);

  useEffect(() => {
    if (!editorMode) {
      indexForStart = 0;
      setIndex(0);
    }
  }, [id, editorMode]);

  useEffect(() => {
    return () => {
      const { editorMode } = useEditorStore.getState();
      if (!editorMode) {
        setIndex(0);
      }
    };
  }, []);

  useGlobalMedia();

  const globalMedia = useGlobalMediaStore();
  const globalPlayer = globalMedia?.player;

  const AddNewItem = () => {
    const allCards = sequence!.items.filter((item: SequenceItem) =>
      matchesItemType(item, itemType)
    );

    let cardElement: SequenceItem | undefined = allCards[index];
    if (!cardElement) {
      cardElement = [...sequence!.items]
        .reverse()
        .find((item: SequenceItem) => matchesItemType(item, itemType));
    }

    if (!cardElement) return;

    const currentPosition = cardElement.position || 0;
    const newPosition = currentPosition + 1;

    const newItem = {
      ...cardElement,
      id: `${Date.now()}`,
      position: newPosition,
      items: [] as SequenceItem[]
    };

    newItem.title = cardElement.title ? 'Type Here' : null;
    newItem.body = cardElement.body ? 'Type Here' : null;

    if (newItemFields?.title !== undefined) {
      newItem.title = newItemFields.title;
    }

    if (newItemFields?.body !== undefined) {
      newItem.body = newItemFields.body;
    }

    const data = {
      type: cardElement.type === 'QUESTION' ? 'Question' : 'Card',
      video_ids: [] as (string | number | undefined)[],
      picture_ids: [] as (string | number | undefined)[],
      audio_ids: [] as (string | number | undefined)[],
      icon: cardElement.icon,
      title: newItem.title,
      body: newItem.body,
      position: newPosition,
      variant: cardElement.variant,
      items: [] as SequenceItem[]
    };

    if (cardElement.videos) {
      data.video_ids.push(cardElement?.videos[0]?.id);
    }

    if (cardElement.pictures) {
      data.picture_ids.push(cardElement?.pictures[0]?.id);
    }

    if (cardElement.audios) {
      data.audio_ids.push(cardElement?.audios[0]?.id);
    }

    const currentIndex = index;
    const nextIndex = currentIndex + 1;

    const collectedItems: Array<{
      data: SubItemStatePayload;
      serverItem: ServerItemResponse;
    }> = [];

    const commitAndNavigate = () => {
      applySubItemsToState(collectedItems);
      setTimeout(() => {
        indexForStart = nextIndex;
        setIndex(nextIndex);
      }, 100);
    };

    addSubItem({
      sequenceId: sequence!.id,
      data,
      newItem: newItem,
      skipStateUpdate: true,
      onCreated: (serverItem: ServerItemResponse) => {
        collectedItems.push({ data: { data, newItem }, serverItem });

        if (cardElement.type !== 'QUESTION' || !cardElement.items?.length) {
          commitAndNavigate();
          return;
        }

        const realId = serverItem.subitem.id;

        const createSequentially = (
          items: typeof cardElement.items,
          itemIndex: number
        ) => {
          if (itemIndex >= items.length) {
            commitAndNavigate();
            return;
          }

          const item = items[itemIndex];
          const childItem = {
            ...item,
            id: `${Date.now()}`,
            parent_id: realId,
            video_ids:
              item.videos?.map((video: { id: string | number }) => video.id) ??
              [],
            picture_ids:
              item.pictures?.map(
                (picture: { id: string | number }) => picture.id
              ) ?? [],
            audio_ids:
              item.audios?.map((audio: { id: string | number }) => audio.id) ??
              [],
            title: item.value
          };

          addSubItem({
            sequenceId: sequence!.id,
            data: childItem,
            newItem: childItem,
            skipStateUpdate: true,
            onCreated: (childServerItem: ServerItemResponse) => {
              collectedItems.push({
                data: { data: childItem, newItem: childItem },
                serverItem: childServerItem
              });
              createSequentially(items, itemIndex + 1);
            },
            onError: () => {
              commitAndNavigate();
            }
          });
        };

        createSequentially(cardElement.items, 0);
      }
    });

    indexForStart = allCards[index] ? nextIndex : allCards.length;
  };

  const handleDelete = () => {
    const allCards = sequence!.items.filter((item: SequenceItem) =>
      matchesItemType(item, itemType)
    );

    const card = allCards[index];

    if (card) {
      removeSubItem(card.id);
      if (index > 0) {
        indexForStart = index - 1;
      } else {
        indexForStart = 0;
      }
    }
  };

  const handleReorder = () => {
    setShowPopup(true);
  };

  return (
    <Wrapper
      variant={variant}
      p={{ _: 0, md: 'lg' }}
      gap={{ _: 'xs', sm: 'sm', md: 'lg' }}
      aria-live="polite"
    >
      {header && header.title && (
        <>
          <x.h1
            display={'block'}
            zIndex={100}
            pt="50px"
            textAlign={'center'}
            minWidth="100%"
            pl={{ _: '20px' }}
            dangerouslySetInnerHTML={{ __html: header.title }}
          />
          <x.span
            position={'absolute'}
            visibility={'hidden'}
          >{t`The cards will appear sequentially`}</x.span>
        </>
      )}
      {(variant === 'horizontal' || variant === 'vertical') && [
        <IconButton
          key="prev"
          onClick={() => {
            if (globalPlayer) {
              try {
                globalPlayer.pause();
              } catch (error) {
                // Silently ignore player operation errors
              }
            }

            setTimeout(() => {
              prev();
              if (!editorMode) {
                try {
                  playAudioEffect();
                } catch (error) {
                  // Silently ignore audio effect errors
                }
              }
            }, 0);
          }}
          aria-label={t`Previous`}
          gridArea="prev"
          transform
          rotate={variant === 'horizontal' ? '180deg' : '-90deg'}
          justifySelf={variant === 'horizontal' ? 'end' : 'start'}
          alignSelf={variant === 'horizontal' ? 'center' : 'end'}
          disabled={index === 0}
        />,
        <IconButton
          key="next"
          onClick={() => {
            if (globalPlayer) {
              try {
                globalPlayer.pause();
              } catch (error) {
                // Silently ignore player operation errors
              }
            }

            setTimeout(() => {
              next();
              if (!editorMode) {
                try {
                  playAudioEffect();
                } catch (error) {
                  // Silently ignore audio effect errors
                }
              }
            }, 0);
          }}
          aria-label={t`Next`}
          gridArea="next"
          transform
          rotate={variant === 'horizontal' ? '0deg' : '90deg'}
          justifySelf="start"
          alignSelf={variant === 'horizontal' ? 'center' : 'start'}
          disabled={index === childArray.length - 1}
        />
      ]}
      {variant === 'horizontal' && showProgressBar && (
        <ProgressBar current={index} total={childArray.length} />
      )}
      {childArray.map((c, i) => {
        const isHidden = variant !== 'dynamic' && index !== i;
        return (
          <CarouselItem
            key={i}
            variant={variant}
            index={i}
            current={index}
            total={childArray.length}
            disabled={variant === 'stack' && index !== i ? true : false}
            aria-hidden={isHidden}
            inert={isHidden}
            editorModeParam={editorMode}
            cssStylesForItem={cssStylesForItem}
          >
            {c}
            {index == i && <p role="alert" aria-atomic="true"></p>}
          </CarouselItem>
        );
      })}
      {contentEditable &&
        createPortal(
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              width: '100%',
              zIndex: 199
            }}
          >
            <div
              style={{
                position: 'fixed',
                bottom: '0',
                marginBottom: '20px',
                zIndex: 199,
                display: 'flex',
                alignItems: 'center'
              }}
            >
              {sequence && sequence.items && sequence.items.length > 2 && (
                <LightButton
                  style={{
                    minWidth: '140px',
                    maxWidth: '140px',
                    zIndex: 2,
                    height: '34px'
                  }}
                  onClick={handleReorder}
                >
                  Reorder
                </LightButton>
              )}
              {MAX_CARDS_COUNT >
                sequence!.items.filter(
                  item => item.type == 'CARD' || item.type == 'QUESTION'
                ).length && (
                <LightButton
                  data-test-id="add-card-btn"
                  style={{
                    minWidth: '140px',
                    maxWidth: '140px',
                    zIndex: 2,
                    height: '34px',
                    marginLeft: '5px'
                  }}
                  onClick={AddNewItem}
                >
                  Add Card
                </LightButton>
              )}
              {sequence &&
                sequence.items &&
                sequence.items.filter(
                  item => item.type == 'CARD' || item.type == 'QUESTION'
                ).length > 1 && (
                  <LightButton
                    title="Remove"
                    onClick={handleDelete}
                    style={{
                      padding: '11px',
                      width: '38px',
                      height: '34px',
                      marginLeft: '5px'
                    }}
                  >
                    <TrashIconForBtn
                      style={{ marginLeft: '-5px', marginTop: '-5px' }}
                    />
                  </LightButton>
                )}
            </div>
            {showPopup &&
              createPortal(
                <PopupWrapper>
                  <Popup className="reorder-popup">
                    <div className="reorder-popup-header">
                      <b>Reorder cards</b>
                      <CloseButton
                        type="close"
                        onClick={() => setShowPopup(false)}
                        autoFocus={false}
                        disabled={disabledBtn}
                      />
                    </div>
                    <div className="reorder-popup-content">
                      {localCardOrder.map(
                        (
                          cardData: { id: number | string; position: number },
                          displayIndex: number
                        ) => {
                          const item = sequence!.items.find(
                            (item: SequenceItem) => item.id === cardData.id
                          );
                          if (!item || !matchesItemType(item, itemType)) {
                            return null;
                          }

                          return (
                            <ReorderComponent
                              key={item.id}
                              title={(item.title || item.body) as string}
                              currentPosition={displayIndex}
                              draggedIndex={draggedIndex}
                              dragOverIndex={dragOverIndex}
                              onDragStart={handleDragStart}
                              onDragOver={handleDragOver}
                              onDragEnd={handleDragEnd}
                              onDrop={handleDrop}
                            />
                          );
                        }
                      )}
                    </div>
                    <Button
                      onClick={applyChanges}
                      disabled={disabledBtn}
                      className="doneButton"
                    >
                      Done
                    </Button>
                  </Popup>
                </PopupWrapper>,
                document.body
              )}
          </div>,
          document.getElementById('mainContentInTheCourseBuilder') ||
            document.body
        )}
    </Wrapper>
  );
};
